import { Box } from '@chakra-ui/react';
import { RippleBg } from '@ionq/ui/assets';
import { isValidElement, type ReactNode } from 'react';

/** Names the content column's left edge (the rail's current width) on the
 * shell root, so textures in the rail band and the top bar can share one
 * viewport-wide pattern. */
export const SHELL_COLUMN_LEFT_VAR = '--shell-column-left';

/** The top bar's trailing controls' width (bell, account menu…), on the
 * shell root: the rail's pinned switcher stops short of them. */
export const SHELL_NAVBAR_END_VAR = '--shell-navbar-end';

/** The @ionq/ui Card ripple as the shell's top-band texture: one
 * viewport-wide pattern drawn by both the rail band (offset 0) and the top
 * bar (offset = the column's left edge), so the two read as a single band
 * with no seam, collapse animation included. Under its host's content (z
 * -1; the host must be a stacking context) in its own clip box. */
export function ShellTexture({ offset }: { offset: 0 | 'column' }) {
    return (
        <Box
            aria-hidden
            position='absolute'
            inset='0'
            overflow='hidden'
            // Stronger in light: the gray lines barely read on #fafafa.
            // Raised for the three-quarter scale, whose rings (filled
            // slivers) render thinner than a pixel.
            opacity={{ _light: 0.2, _dark: 0.11 }}
            zIndex={-1}
            pointerEvents='none'
        >
            <div style={offset === 0 ? RAIL_STYLE : COLUMN_STYLE} />
        </Box>
    );
}

// The ripple as a CSS mask over a --muted-foreground fill (the kit's ring
// colour), tiled at about three-quarter scale: one plain box per band
// instead of a ~180-path SVG per tile, so the compositor tiles one raster.
// Serialized once from the kit's own element tree (no DOM); its card frame
// is cropped off the viewBox so tiles butt without seams of rule.
const TILE_WIDTH = 720; // px

const SVG_ATTRS: Record<string, string> = {
    fillRule: 'fill-rule',
    clipRule: 'clip-rule',
};

function toMarkup(node: ReactNode): string {
    if (Array.isArray(node)) return node.map(toMarkup).join('');
    if (!isValidElement(node) || typeof node.type !== 'string') return '';
    const { children, style, ...attrs } = node.props as Record<string, unknown>;
    let out = `<${node.type}`;
    for (const [name, value] of Object.entries(attrs)) {
        if (value != null) out += ` ${SVG_ATTRS[name] ?? name}="${value}"`;
    }
    if (style) {
        const css = Object.entries(style as Record<string, string>)
            .map(([k, v]) => `${k.replace(/[A-Z]/g, '-$&').toLowerCase()}:${v}`)
            .join(';');
        out += ` style="${css}"`;
    }
    return `${out}>${toMarkup(children as ReactNode)}</${node.type}>`;
}

// Alpha only: rings and the frame's crop opaque, nothing stroked.
const RIPPLE_MASK = `url("data:image/svg+xml,${encodeURIComponent(
    toMarkup(RippleBg({ viewBox: '2 2 951 232' }))
        .replaceAll('var(--muted-foreground)', '#000')
        .replaceAll('var(--background)', '#000')
        .replaceAll('var(--border)', 'none'),
)}")`;

// Own compositor layers (will-change): the rail band re-clipping it and
// the top bar sliding it (transform, not left) during the collapse
// animation stay compositor-only.
const RAIL_STYLE = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100%',
    background: 'var(--muted-foreground)',
    maskImage: RIPPLE_MASK,
    maskSize: `${TILE_WIDTH}px auto`,
    maskRepeat: 'repeat-x',
    maskPosition: '0 50%',
    willChange: 'transform',
} as const;

const COLUMN_STYLE = {
    ...RAIL_STYLE,
    transform: `translateX(calc(-1 * var(${SHELL_COLUMN_LEFT_VAR}, 0px)))`,
} as const;
